import React, { useContext } from "react";
import { RESOURCE_NAME } from "common/authorization";
import {
  CustomObjectGroup,
  useDeleteCustomObjectGroupMutation,
  useUpdateCustomObjectGroupMutation,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { AuthContext } from "../../../Authorization/AuthContext";
import { useDeleteModal } from "../../../Common/DeleteModal";
import DropdownMenu, { ActionsProps } from "../../../Inputs/DropdownMenu";
import { DeleteModalContent } from "./__styles__/CustomObjectGroup";
import { useUpsertCustomObjectGroupModal } from "./upsertCustomObjectGroupModal";

export type ActionButtonProps = {
  customObjectGroup: Pick<
    CustomObjectGroup,
    "id" | "name" | "customMapIds" | "hasTable"
  >;
  onUpdateSuccess: () => void;
  onDeleteSuccess: () => void;
};

export const ActionButton = ({
  customObjectGroup,
  onUpdateSuccess,
  onDeleteSuccess,
}: ActionButtonProps) => {
  const { authorized } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const canEditCustomObjectGroup = authorized({
    resource: RESOURCE_NAME.CUSTOM_OBJECT_GROUP,
    permission: "update",
  });

  const canDeleteCustomObjectGroup = authorized({
    resource: RESOURCE_NAME.CUSTOM_OBJECT_GROUP,
    permission: "delete",
  });

  const [deletecustomObjectGroup, { loading: deleteLoading }] =
    useDeleteCustomObjectGroupMutation({
      onCompleted: () => {
        addSuccessToast("Your object group was successfully deleted");
        onDeleteSuccess();
      },
      onError: () => {
        addErrorToast(
          "There was a problem deleting the object group. Please try again or contact support@withforerunner.com",
        );
      },
    });

  const [updateCustomObjectGroupMutation, { loading: updateLoading }] =
    useUpdateCustomObjectGroupMutation();

  const [showUpsertCustomObjectGroupModal] = useUpsertCustomObjectGroupModal({
    onSubmit: updateCustomObjectGroupMutation,
    refetch: onUpdateSuccess,
  });

  const [showDeletecustomObjectGroupModal] = useDeleteModal({
    headerText: "Delete object group",
    subheaderText: customObjectGroup.name,
    Content: (
      <p>
        <DeleteModalContent>
          Are you sure you would like to delete this object group? If an object
          table was created, this will also be removed.{" "}
        </DeleteModalContent>
        <DeleteModalContent bold>
          This action cannot be undone.
        </DeleteModalContent>
      </p>
    ),
    deleteButtonText: "Delete",
    onDelete: () => {
      void deletecustomObjectGroup({
        variables: { id: customObjectGroup.id },
      });
    },
  });

  const deleteDisabled = !canDeleteCustomObjectGroup || deleteLoading;
  const editDisabled = !canEditCustomObjectGroup || updateLoading;

  const actions: Array<ActionsProps> = [
    {
      label: "Edit group",
      disabled: editDisabled,
      onClick: () => {
        showUpsertCustomObjectGroupModal(customObjectGroup);
      },
    },
    {
      label: "Delete",
      variant: "red",
      disabled: deleteDisabled,
      onClick: showDeletecustomObjectGroupModal,
    },
  ];

  return <DropdownMenu actions={actions} isTableAction />;
};
